/**
* Per-language editorial notes powering /translation/lang/$lang.
* Each entry is hand-written so every language page carries unique copy
* (embassy/authority context, script/formatting caveats, price band,
* and the document types most requested for that language).
*/

export interface MltLanguageNote {
  /** Embassy or authority most often involved for this language pair. */
  authority: string;
  /** Script / typography / formatting caveat specific to this language. */
  script: string;
  /** Indicative price band per page (THB). */
  rate: string;
  /** Typical turnaround. */
  eta: string;
  /** Most requested document types (free text, Thai). */
  common: string[];
  /** One distinctive advisory paragraph. */
  note: string;
}

export const MLT_LANGUAGE_NOTES: Record<string, MltLanguageNote> = {
  english: {
    authority: "กรมการกงสุล (แจ้งวัฒนะ) และสถานทูตกลุ่มประเทศที่ใช้ภาษาอังกฤษ",
    script: "ต้องสะกดชื่อ-นามสกุลให้ตรงกับหน้าพาสปอร์ตทุกตัวอักษร รวมถึงคำนำหน้าและลำดับชื่อกลาง",
    rate: "สอบถามค่าบริการ",
    eta: "1-2 วันทำการ (ด่วนภายในวันเดียวได้)",
    common: ["สูติบัตร", "ทะเบียนสมรส", "ทะเบียนบ้าน", "ทรานสคริปต์", "หนังสือรับรองบริษัท"],
    note: "ภาษาอังกฤษเป็นคู่ภาษาที่กรมการกงสุลรับรองเร็วที่สุด และเป็นภาษากลางที่สถานทูตเกือบทุกแห่งยอมรับ หากปลายทางไม่ได้ระบุภาษาเฉพาะ แนะนำให้แปลเป็นอังกฤษก่อนเสมอเพื่อประหยัดเวลาและค่าใช้จ่าย",
  },
  chinese: {
    authority: "สถานเอกอัครราชทูตสาธารณรัฐประชาชนจีน / CVASC กรุงเทพฯ",
    script: "เลือกตัวเต็ม (ไต้หวัน ฮ่องกง มาเก๊า) หรือตัวย่อ (จีนแผ่นดินใหญ่) ให้ตรงกับปลายทาง และต้องถอดชื่อเป็นอักษรจีนอย่างสม่ำเสมอทุกฉบับ",
    rate: "สอบถามค่าบริการ",
    eta: "2-3 วันทำการ",
    common: ["ทะเบียนสมรส", "ใบรับรองโสด", "หนังสือรับรองบริษัท", "ใบตรวจประวัติอาชญากรรม", "ปริญญาบัตร"],
    note: "งานยื่นจีนแผ่นดินใหญ่ต้องผ่านกรมการกงสุลไทยก่อน แล้วจึงรับรองที่สถานทูตจีน ส่วนไต้หวันใช้สำนักงานเศรษฐกิจและวัฒนธรรมไทเป การเลือกช่องทางผิดทำให้ต้องเริ่มใหม่ทั้งชุด",
  },
  japanese: {
    authority: "สถานเอกอัครราชทูตญี่ปุ่นประจำประเทศไทย",
    script: "ต้องเขียนชื่อเป็นคาตาคานะให้ตรงกับที่เคยใช้ในเอกสารญี่ปุ่นเดิม และใช้ปีศักราชญี่ปุ่น (令和) กำกับคู่กับ ค.ศ. เมื่อจำเป็น",
    rate: "สอบถามค่าบริการ",
    eta: "2-4 วันทำการ",
    common: ["ทะเบียนสมรส", "สูติบัตร", "ใบรับรองโสด", "ทะเบียนบ้าน", "ใบรับรองการทำงาน"],
    note: "สำนักงานเขต (市役所) ในญี่ปุ่นตรวจรายละเอียดชื่อและวันที่อย่างเข้มงวด เราจึงทำตารางเทียบชื่อ-วันที่แนบไปกับคำแปลทุกครั้ง เพื่อให้เจ้าหน้าที่ตรวจสอบได้ทันที",
  },
  korean: {
    authority: "สถานเอกอัครราชทูตสาธารณรัฐเกาหลี / KVAC กรุงเทพฯ",
    script: "ถอดชื่อเป็นฮันกึลตามระบบ Revised Romanization และคงลำดับนามสกุล-ชื่อแบบเกาหลี",
    rate: "สอบถามค่าบริการ",
    eta: "2-4 วันทำการ",
    common: ["ทะเบียนสมรส", "ใบรับรองโสด", "ใบตรวจประวัติอาชญากรรม", "ทรานสคริปต์", "สเตทเมนต์ธนาคาร"],
    note: "งานสมรสและวีซ่า F-6 ของเกาหลีต้องใช้ฉบับแปลเกาหลีที่ผ่านกรมการกงสุล และมักขอเอกสารอายุไม่เกิน 3 เดือน ควรวางแผนคิวแปลและรับรองให้จบภายในเดือนเดียว",
  },
  german: {
    authority: "สถานเอกอัครราชทูตเยอรมนี ประจำกรุงเทพฯ",
    script: "ต้องคงอักขระ ä ö ü ß ให้ถูกต้อง และใช้รูปแบบวันที่ DD.MM.YYYY",
    rate: "สอบถามค่าบริการ",
    eta: "3-5 วันทำการ",
    common: ["สูติบัตร", "ใบรับรองโสด", "ทะเบียนสมรส", "ใบหย่า", "ปริญญาบัตร"],
    note: "เยอรมนีมักกำหนดให้แปลโดยนักแปลที่สถานทูตยอมรับ และเอกสารไทยต้องผ่านกรมการกงสุลก่อนนำไปรับรองที่สถานทูตเยอรมนี เราจัดคิวทั้งสองขั้นตอนต่อเนื่องกันให้",
  },
  french: {
    authority: "สถานเอกอัครราชทูตฝรั่งเศส / TLScontact กรุงเทพฯ",
    script: "ต้องใช้เครื่องหมายกำกับเสียง (é è ê ç) ครบถ้วน และแปลชื่อหน่วยงานไทยตามศัพท์ราชการฝรั่งเศส",
    rate: "สอบถามค่าบริการ",
    eta: "3-5 วันทำการ",
    common: ["ทะเบียนสมรส", "สูติบัตร", "ใบรับรองโสด", "ทะเบียนบ้าน", "คำพิพากษาศาล"],
    note: "ฝรั่งเศสยอมรับเฉพาะคำแปลจากนักแปลที่ขึ้นทะเบียนกับสถานทูต (traducteur agréé) เราส่งงานผ่านเครือข่ายนักแปลที่อยู่ในบัญชีปัจจุบันเท่านั้น",
  },
  spanish: {
    authority: "สถานเอกอัครราชทูตสเปน และสถานทูตกลุ่มลาตินอเมริกา",
    script: "แยกสำเนวนสเปนยุโรปกับลาตินอเมริกา และคงระบบนามสกุลคู่ (apellido paterno / materno)",
    rate: "สอบถามค่าบริการ",
    eta: "3-5 วันทำการ",
    common: ["ทะเบียนสมรส", "สูติบัตร", "ใบรับรองโสด", "ปริญญาบัตร", "หนังสือมอบอำนาจ"],
    note: "หลายประเทศที่ใช้ภาษาสเปนเป็นภาคีอนุสัญญากรุงเฮก จึงใช้ Apostille แทนการรับรองสถานทูตได้ ช่วยลดเวลาลงหนึ่งขั้นตอนเต็ม",
  },
  russian: {
    authority: "สถานเอกอัครราชทูตสหพันธรัฐรัสเซีย ประจำกรุงเทพฯ",
    script: "ถอดชื่อเป็นซีริลลิกตามระบบ GOST และต้องระบุรูปเพศของคำลงท้ายนามสกุลให้ถูกต้อง",
    rate: "สอบถามค่าบริการ",
    eta: "3-6 วันทำการ",
    common: ["ทะเบียนสมรส", "สูติบัตร", "ใบหย่า", "ใบตรวจประวัติอาชญากรรม", "ใบรับรองแพทย์"],
    note: "เอกสารที่ยื่นในรัสเซียมักต้องมี Notary รับรองลายมือชื่อนักแปลด้วย เราจึงจัดชุดแปล + Notary + กรมการกงสุลไปพร้อมกันเพื่อลดรอบเดินเรื่อง",
  },
  italian: {
    authority: "สถานเอกอัครราชทูตอิตาลี ประจำกรุงเทพฯ",
    script: "ต้องใช้ศัพท์ทะเบียนราษฎรอิตาลี (stato civile) และรูปแบบวันที่ DD/MM/YYYY",
    rate: "สอบถามค่าบริการ",
    eta: "4-6 วันทำการ",
    common: ["ทะเบียนสมรส", "สูติบัตร", "ใบรับรองโสด", "ใบหย่า", "หนังสือรับรองบริษัท"],
    note: "อิตาลีกำหนดขั้นตอน legalizzazione ที่เข้มงวด และมักขอ nulla osta ประกอบ เราตรวจข้อกำหนดของ comune ปลายทางก่อนเริ่มงานทุกครั้ง",
  },
  arabic: {
    authority: "สถานทูตกลุ่มประเทศอาหรับ (UAE, ซาอุดีอาระเบีย, กาตาร์, คูเวต)",
    script: "จัดวางจากขวาไปซ้าย (RTL) และต้องถอดชื่อเป็นอักษรอาหรับให้ตรงกับที่เคยใช้ในวีซ่าเดิม",
    rate: "สอบถามค่าบริการ",
    eta: "4-7 วันทำการ",
    common: ["ปริญญาบัตร", "ใบตรวจประวัติอาชญากรรม", "ทะเบียนสมรส", "ใบรับรองแพทย์", "สัญญาจ้างงาน"],
    note: "งานตะวันออกกลางเกือบทั้งหมดต้องผ่านกรมการกงสุลไทยแล้วรับรองที่สถานทูตปลายทาง และบางประเทศเก็บค่าธรรมเนียมตามมูลค่าสัญญา ควรประเมินค่าใช้จ่ายล่วงหน้า",
  },
  vietnamese: {
    authority: "สถานเอกอัครราชทูตเวียดนาม ประจำกรุงเทพฯ",
    script: "ต้องคงวรรณยุกต์เวียดนาม (dấu) ครบถ้วน มิฉะนั้นชื่อจะถูกอ่านผิดความหมาย",
    rate: "สอบถามค่าบริการ",
    eta: "3-5 วันทำการ",
    common: ["ทะเบียนสมรส", "สูติบัตร", "ใบรับรองโสด", "หนังสือรับรองบริษัท", "สัญญาทางการค้า"],
    note: "งานลงทุนและจดทะเบียนธุรกิจในเวียดนามมักขอชุดเอกสารบริษัทครบชุดพร้อมรับรองกงสุล เราจัดทำเป็นแพ็กเกจรายชุดเพื่อคุมต้นทุนต่อฉบับ",
  },
  myanmar: {
    authority: "สถานเอกอัครราชทูตเมียนมา ประจำกรุงเทพฯ",
    script: "ใช้ฟอนต์ Unicode (ไม่ใช่ Zawgyi) และถอดชื่อตามที่ปรากฏในบัตรประชาชนเมียนมา",
    rate: "สอบถามค่าบริการ",
    eta: "3-6 วันทำการ",
    common: ["หนังสือเดินทาง", "สัญญาจ้างงาน", "ทะเบียนสมรส", "สูติบัตร", "ใบรับรองแพทย์"],
    note: "งานแรงงานเมียนมามักยื่นเป็นชุดใหญ่พร้อมกันหลายคน เรารองรับงานองค์กรแบบรายชุดพร้อมตารางตรวจสอบชื่อทีละราย",
  },
  lao: {
    authority: "สถานเอกอัครราชทูต สปป.ลาว ประจำกรุงเทพฯ",
    script: "อักษรลาวใกล้เคียงไทยแต่ศัพท์ราชการต่างกัน ต้องใช้คำทางการของลาว ไม่ใช่การทับศัพท์ตรงตัว",
    rate: "สอบถามค่าบริการ",
    eta: "2-4 วันทำการ",
    common: ["ทะเบียนสมรส", "สูติบัตร", "ทะเบียนบ้าน", "สัญญาจ้างงาน", "หนังสือมอบอำนาจ"],
    note: "เอกสารข้ามแดนไทย-ลาวมักใช้คู่กับหนังสือมอบอำนาจ เราจัดทำฉบับสองภาษาแบบคู่ขนานเพื่อให้เจ้าหน้าที่ทั้งสองฝั่งตรวจได้ในแผ่นเดียว",
  },
  khmer: {
    authority: "สถานเอกอัครราชทูตกัมพูชา ประจำกรุงเทพฯ",
    script: "ต้องใช้อักษรเขมรมาตรฐาน และระวังการถอดชื่อที่มีรากบาลี-สันสกฤตให้ตรงกับเอกสารเดิม",
    rate: "สอบถามค่าบริการ",
    eta: "3-5 วันทำการ",
    common: ["ทะเบียนสมรส", "สูติบัตร", "หนังสือเดินทาง", "สัญญาจ้างงาน", "หนังสือรับรองบริษัท"],
    note: "งานกัมพูชามักต้องรับรองทั้งกระทรวงการต่างประเทศไทยและกัมพูชา เราประสานทั้งสองฝั่งและแจ้งกำหนดวันรับเอกสารล่วงหน้า",
  },
  indonesian: {
    authority: "สถานเอกอัครราชทูตอินโดนีเซีย ประจำกรุงเทพฯ",
    script: "ใช้ Bahasa Indonesia แบบทางการ (EYD) และระวังชื่อที่ไม่มีนามสกุลตามธรรมเนียมอินโดนีเซีย",
    rate: "สอบถามค่าบริการ",
    eta: "3-5 วันทำการ",
    common: ["ทะเบียนสมรส", "ใบรับรองโสด", "หนังสือรับรองบริษัท", "ปริญญาบัตร", "สัญญาทางการค้า"],
    note: "อินโดนีเซียเข้าเป็นภาคีอนุสัญญากรุงเฮก จึงใช้ Apostille แทนการรับรองสถานทูตได้ในหลายกรณี ควรยืนยันกับหน่วยงานปลายทางก่อนเลือกเส้นทาง",
  },
  malay: {
    authority: "สถานเอกอัครราชทูตมาเลเซีย ประจำกรุงเทพฯ",
    script: "ใช้ Bahasa Malaysia และคงคำนำหน้าเกียรติยศ (Dato', Haji) ตามต้นฉบับ",
    rate: "สอบถามค่าบริการ",
    eta: "3-5 วันทำการ",
    common: ["ทะเบียนสมรส", "ใบรับรองโสด", "หนังสือรับรองบริษัท", "ทะเบียนบ้าน", "ใบขับขี่"],
    note: "งานสมรสข้ามแดนไทย-มาเลเซียมักต้องใช้เอกสารศาสนาประกอบด้วย เราตรวจสอบข้อกำหนดของรัฐปลายทางซึ่งแตกต่างกันในแต่ละรัฐ",
  },
  tagalog: {
    authority: "สถานเอกอัครราชทูตฟิลิปปินส์ ประจำกรุงเทพฯ",
    script: "เอกสารราชการฟิลิปปินส์ส่วนใหญ่ใช้อังกฤษคู่ตากาล็อก ควรทำฉบับสองภาษาเพื่อลดข้อโต้แย้ง",
    rate: "สอบถามค่าบริการ",
    eta: "3-6 วันทำการ",
    common: ["ทะเบียนสมรส", "สูติบัตร", "CENOMAR", "สัญญาจ้างงาน", "ใบรับรองแพทย์"],
    note: "ฟิลิปปินส์เป็นภาคี Apostille แล้ว เอกสารไทยที่ผ่านกรมการกงสุลและติด Apostille ใช้ได้ทันทีโดยไม่ต้องรับรองสถานทูตอีก",
  },
  dutch: {
    authority: "สถานเอกอัครราชทูตเนเธอร์แลนด์ ประจำกรุงเทพฯ",
    script: "คงคำนำหน้านามสกุล (van, de) เป็นตัวพิมพ์เล็กตามธรรมเนียมดัตช์",
    rate: "สอบถามค่าบริการ",
    eta: "4-6 วันทำการ",
    common: ["ทะเบียนสมรส", "ใบรับรองโสด", "สูติบัตร", "ใบหย่า", "สเตทเมนต์ธนาคาร"],
    note: "เทศบาลเนเธอร์แลนด์ (gemeente) ตรวจ Apostille และวันที่ออกเอกสารอย่างเคร่งครัด มักไม่รับเอกสารที่ออกเกิน 6 เดือน",
  },
  swedish: {
    authority: "สถานเอกอัครราชทูตสวีเดน ประจำกรุงเทพฯ",
    script: "คงอักขระ å ä ö และใช้รูปแบบวันที่ YYYY-MM-DD ตามมาตรฐานสวีเดน",
    rate: "สอบถามค่าบริการ",
    eta: "4-6 วันทำการ",
    common: ["ทะเบียนสมรส", "ใบรับรองโสด", "สูติบัตร", "ใบตรวจประวัติอาชญากรรม", "สัญญาจ้างงาน"],
    note: "Skatteverket ของสวีเดนต้องการเอกสารที่ผ่าน Apostille และคำแปลที่ระบุชื่อผู้แปลชัดเจน เราแนบใบรับรองความถูกต้องของคำแปลทุกฉบับ",
  },
  danish: {
    authority: "สถานเอกอัครราชทูตเดนมาร์ก ประจำกรุงเทพฯ",
    script: "คงอักขระ æ ø å และเรียงชื่อตามระบบทะเบียน CPR",
    rate: "สอบถามค่าบริการ",
    eta: "4-6 วันทำการ",
    common: ["ทะเบียนสมรส", "ใบรับรองโสด", "สูติบัตร", "ใบหย่า", "ใบรับรองการทำงาน"],
    note: "การจดทะเบียนสมรสในเดนมาร์กมีคิวออนไลน์ที่กำหนดวันแน่นอน เราจึงวางแผนย้อนกลับจากวันนัดเพื่อให้เอกสารทันเสมอ",
  },
  finnish: {
    authority: "สถานเอกอัครราชทูตฟินแลนด์ ประจำกรุงเทพฯ",
    script: "ภาษาฟินแลนด์มีระบบการผันคำ (case) จำนวนมาก ชื่อสถานที่ในคำแปลต้องผันให้ถูกบริบท",
    rate: "สอบถามค่าบริการ",
    eta: "5-7 วันทำการ",
    common: ["ทะเบียนสมรส", "ใบรับรองโสด", "สูติบัตร", "ทรานสคริปต์", "สเตทเมนต์ธนาคาร"],
    note: "หน่วยงาน DVV ของฟินแลนด์ยอมรับคำแปลอังกฤษในหลายกรณี การเลือกแปลอังกฤษแทนฟินแลนด์จึงอาจประหยัดทั้งเวลาและค่าใช้จ่าย",
  },
  norwegian: {
    authority: "สถานเอกอัครราชทูตนอร์เวย์ ประจำกรุงเทพฯ",
    script: "เลือกรูปแบบ Bokmål (ใช้ทั่วไป) หรือ Nynorsk ตามที่หน่วยงานปลายทางกำหนด",
    rate: "สอบถามค่าบริการ",
    eta: "5-7 วันทำการ",
    common: ["ทะเบียนสมรส", "ใบรับรองโสด", "สูติบัตร", "ใบตรวจประวัติอาชญากรรม", "สัญญาจ้างงาน"],
    note: "UDI ของนอร์เวย์ตรวจสอบสายเอกสารตั้งแต่ต้นฉบับถึงคำแปลอย่างละเอียด เราจึงแนบสำเนาต้นฉบับที่ประทับตราไว้คู่กับคำแปลเสมอ",
  },
  hindi: {
    authority: "สถานเอกอัครราชทูตอินเดีย ประจำกรุงเทพฯ",
    script: "ใช้อักษรเทวนาครีและถอดชื่อให้ตรงกับพาสปอร์ตอินเดีย ซึ่งบางเล่มไม่มีนามสกุล",
    rate: "สอบถามค่าบริการ",
    eta: "4-6 วันทำการ",
    common: ["ทะเบียนสมรส", "สูติบัตร", "ปริญญาบัตร", "ใบตรวจประวัติอาชญากรรม", "หนังสือมอบอำนาจ"],
    note: "งานยื่นในอินเดียมักผ่านระบบ MEA และต้องมี Apostille เราจึงจัดเส้นทาง กรมการกงสุล → Apostille ให้จบก่อนส่งออกนอกประเทศ",
  },
  persian: {
    authority: "สถานเอกอัครราชทูตสาธารณรัฐอิสลามอิหร่าน ประจำกรุงเทพฯ",
    script: "จัดวางแบบ RTL และใช้ปฏิทินฮิจเราะห์สุริยคติกำกับคู่กับ ค.ศ. เมื่อจำเป็น",
    rate: "สอบถามค่าบริการ",
    eta: "5-8 วันทำการ",
    common: ["ทะเบียนสมรส", "สูติบัตร", "ปริญญาบัตร", "ใบรับรองแพทย์", "หนังสือรับรองบริษัท"],
    note: "เอกสารที่ใช้ในอิหร่านมักต้องรับรองโดยสถานทูตและกระทรวงการต่างประเทศปลายทาง ควรเผื่อเวลาเดินเรื่องมากกว่าคู่ภาษายุโรปประมาณหนึ่งเท่าตัว",
  },
  turkish: {
    authority: "สถานเอกอัครราชทูตตุรกี ประจำกรุงเทพฯ",
    script: "คงอักขระ ç ğ ı ş ü ö และระวังตัว i กับ ı ซึ่งเป็นคนละตัวอักษรในภาษาตุรกี",
    rate: "สอบถามค่าบริการ",
    eta: "4-7 วันทำการ",
    common: ["ทะเบียนสมรส", "ใบรับรองโสด", "สูติบัตร", "ปริญญาบัตร", "สัญญาทางการค้า"],
    note: "ตุรกีเป็นภาคีอนุสัญญากรุงเฮก แต่ยังนิยมให้ Notary ท้องถิ่น (noter) รับรองคำแปลอีกชั้นหนึ่ง ควรเตรียมงบสำหรับขั้นตอนปลายทางด้วย",
  },
  portuguese: {
    authority: "สถานเอกอัครราชทูตโปรตุเกส และสถานทูตบราซิล ประจำกรุงเทพฯ",
    script: "แยกสำเนวนโปรตุเกสยุโรปกับบราซิล เพราะศัพท์ทะเบียนราษฎรต่างกันชัดเจน",
    rate: "สอบถามค่าบริการ",
    eta: "4-6 วันทำการ",
    common: ["ทะเบียนสมรส", "สูติบัตร", "ใบรับรองโสด", "ปริญญาบัตร", "หนังสือมอบอำนาจ"],
    note: "งานบราซิลมักต้องมี Apostille และคำแปลโดย tradutor juramentado ในบราซิล เราจึงเตรียมฉบับอังกฤษคู่ไว้ให้เพื่อรองรับขั้นตอนปลายทาง",
  },
  polish: {
    authority: "สถานเอกอัครราชทูตโปแลนด์ ประจำกรุงเทพฯ",
    script: "คงอักขระ ą ć ę ł ń ó ś ź ż และผันนามสกุลตามเพศตามหลักภาษาโปแลนด์",
    rate: "สอบถามค่าบริการ",
    eta: "5-7 วันทำการ",
    common: ["ทะเบียนสมรส", "ใบรับรองโสด", "สูติบัตร", "ใบตรวจประวัติอาชญากรรม", "ทรานสคริปต์"],
    note: "สำนักทะเบียน USC ของโปแลนด์มักขอคำแปลจาก tłumacz przysięgły เราจึงจัดเตรียมฉบับอังกฤษที่ Apostille แล้วเพื่อให้แปลรับรองต่อที่ปลายทางได้ทันที",
  },
  czech: {
    authority: "สถานเอกอัครราชทูตสาธารณรัฐเช็ก ประจำกรุงเทพฯ",
    script: "คงเครื่องหมาย háček (č š ž ř) และผันนามสกุลหญิงด้วยคำลงท้าย -ová เมื่อปลายทางกำหนด",
    rate: "สอบถามค่าบริการ",
    eta: "5-7 วันทำการ",
    common: ["ทะเบียนสมรส", "ใบรับรองโสด", "สูติบัตร", "ใบหย่า", "หนังสือรับรองบริษัท"],
    note: "เอกสารสำหรับเช็กต้องมี Apostille และคำแปลรับรองโดยล่ามที่ศาลแต่งตั้ง เราให้คำแนะนำเรื่องลำดับขั้นตอนก่อนส่งเอกสารออกนอกประเทศ",
  },
  hebrew: {
    authority: "สถานเอกอัครราชทูตอิสราเอล ประจำกรุงเทพฯ",
    script: "จัดวางแบบ RTL ถอดชื่อเป็นอักษรฮีบรู และระบุวันที่ตามปฏิทินเกรกอเรียนเป็นหลัก",
    rate: "สอบถามค่าบริการ",
    eta: "5-8 วันทำการ",
    common: ["ทะเบียนสมรส", "สูติบัตร", "ใบรับรองโสด", "สัญญาจ้างงาน", "ใบรับรองแพทย์"],
    note: "งานแรงงานภาคเกษตรและงานสมรสในอิสราเอลต้องผ่าน Apostille และมักขอคำแปลอังกฤษคู่ฮีบรู เราจัดทำสองฉบับพร้อมกันเพื่อลดรอบส่งกลับ",
  },
  nepali: {
    authority: "สถานเอกอัครราชทูตเนปาล ประจำกรุงเทพฯ",
    script: "ใช้อักษรเทวนาครี และเทียบวันที่ระหว่างปฏิทิน Bikram Sambat กับ ค.ศ. ให้ชัดเจน",
    rate: "สอบถามค่าบริการ",
    eta: "5-8 วันทำการ",
    common: ["หนังสือเดินทาง", "ทะเบียนสมรส", "สูติบัตร", "สัญญาจ้างงาน", "ใบตรวจประวัติอาชญากรรม"],
    note: "การเทียบวันเกิดระหว่างปฏิทินเนปาลกับสากลเป็นสาเหตุที่เอกสารถูกตีกลับบ่อยที่สุด เราแนบตารางเทียบวันที่ไว้ท้ายคำแปลทุกฉบับ",
  },
};
